/**
 * Fleet bubble: a floating arrow pointing at the nearest other driver with the
 * distance to them. Tap it to open the roster, pick a driver, and send one of
 * three fixed questions. Their answer comes back as a toast.
 *
 * Everything the driver reads or hears is rendered from an enum on this device,
 * in this device's language and voice - see fleet/fleet.ts for why the wire
 * format carries 'food' | 'fuel' | 'help' rather than a sentence.
 *
 * Designed for glances, not reading: the arrow rotates to point at the other
 * car so its direction is understood without parsing text, targets are large,
 * and the incoming prompt is a two-button yes/no that is also spoken aloud.
 */

import type { FleetContact, MessageKind, MessageReply, QuickMessage } from '../fleet/fleet';
import { formatDistance } from '../core/format';
import type { UnitSystem } from '../core/settings';
import { t } from '../core/i18n';

const KINDS: Array<{ kind: MessageKind; icon: string }> = [
  { kind: 'food', icon: '🍜' },
  { kind: 'fuel', icon: '⛽' },
  { kind: 'help', icon: '🆘' },
];

export interface FleetBubbleActions {
  onSend: (to: number, kind: MessageKind) => void;
  onReply: (id: string, reply: MessageReply) => void;
}

export class FleetBubble {
  readonly el: HTMLElement;
  private sheet: HTMLElement;
  private prompt: HTMLElement;
  private contacts: FleetContact[] = [];
  private units: UnitSystem = 'metric';
  private selected: number | null = null;
  private promptQueue: QuickMessage[] = [];
  private promptTimer: number | null = null;

  constructor(
    parent: HTMLElement,
    private actions: FleetBubbleActions,
  ) {
    this.el = document.createElement('button');
    this.el.className = 'fleet-bubble';
    this.el.hidden = true;
    this.el.setAttribute('aria-label', t('fleet.title'));
    parent.appendChild(this.el);
    this.el.addEventListener('click', () => this.openSheet());

    this.sheet = document.createElement('div');
    this.sheet.className = 'fleet-sheet';
    this.sheet.hidden = true;
    parent.appendChild(this.sheet);

    this.prompt = document.createElement('div');
    this.prompt.className = 'msg-prompt';
    this.prompt.hidden = true;
    parent.appendChild(this.prompt);
  }

  setUnits(units: UnitSystem): void {
    this.units = units;
  }

  /** Refresh the roster. Hides the bubble entirely when nobody else is out. */
  update(contacts: FleetContact[]): void {
    this.contacts = contacts;
    const nearest = contacts[0];
    if (!nearest) {
      this.el.hidden = true;
      if (!this.sheet.hidden) this.renderSheet();
      return;
    }

    this.el.hidden = false;
    this.el.classList.toggle('is-stale', nearest.stale);
    // The arrow points at them relative to where WE are facing, so it reads
    // correctly on a rotating, heading-up map.
    this.el.innerHTML = `
      <span class="fleet-bubble__arrow" style="transform: rotate(${Math.round(nearest.relativeBearing)}deg)">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3 L20 20 L12 15.5 L4 20 Z" fill="currentColor"/></svg>
      </span>
      <span class="fleet-bubble__text">
        <strong>${formatDistance(nearest.distance, this.units)}</strong>
        <em>${escapeHtml(nearest.name)}</em>
      </span>
      ${contacts.length > 1 ? `<span class="fleet-bubble__count">+${contacts.length - 1}</span>` : ''}`;

    if (!this.sheet.hidden) this.renderSheet();
  }

  /* ----------------------------- roster sheet ---------------------------- */

  private openSheet(): void {
    this.selected = this.contacts[0]?.id ?? null;
    this.sheet.hidden = false;
    this.renderSheet();
  }

  closeSheet(): void {
    this.sheet.hidden = true;
  }

  private renderSheet(): void {
    if (!this.contacts.length) {
      this.sheet.innerHTML = `
        <div class="sheet__grab"></div>
        <div class="fleet-sheet__head"><h3>${t('fleet.title')}</h3>
          <button class="icon-btn" data-act="close" aria-label="${t('preview.cancel')}">${X_SVG}</button></div>
        <div class="panel__hint">${t('fleet.nobody')}</div>`;
      this.sheet.querySelector('[data-act=close]')?.addEventListener('click', () => this.closeSheet());
      return;
    }

    const rows = this.contacts
      .map(
        (c) => `
        <button class="fleet-row ${c.id === this.selected ? 'is-selected' : ''} ${c.stale ? 'is-stale' : ''}"
                data-driver="${c.id}">
          <span class="fleet-row__arrow" style="transform: rotate(${Math.round(this.relative(c))}deg)">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3 L20 20 L12 15.5 L4 20 Z" fill="currentColor"/></svg>
          </span>
          <span class="fleet-row__name">${escapeHtml(c.name)}
            ${c.destination ? `<em>${escapeHtml(c.destination)}</em>` : ''}
          </span>
          <span class="fleet-row__dist">${formatDistance(c.distance, this.units)}
            ${c.stale ? `<em>${t('fleet.stale')}</em>` : ''}
          </span>
        </button>`,
      )
      .join('');

    const target = this.contacts.find((c) => c.id === this.selected) ?? this.contacts[0];

    this.sheet.innerHTML = `
      <div class="sheet__grab"></div>
      <div class="fleet-sheet__head">
        <h3>${t('fleet.title')}</h3>
        <button class="icon-btn" data-act="close" aria-label="${t('preview.cancel')}">${X_SVG}</button>
      </div>
      <div class="fleet-rows">${rows}</div>
      <div class="fleet-sheet__ask">${t('fleet.ask', { name: escapeHtml(target.name) })}</div>
      <div class="quick-msgs">
        ${KINDS.map(
          ({ kind, icon }) => `
          <button class="quick-msg quick-msg--${kind}" data-kind="${kind}">
            <span class="quick-msg__icon">${icon}</span>
            <span>${t(`msg.${kind}`)}</span>
          </button>`,
        ).join('')}
      </div>`;

    this.sheet.querySelector('[data-act=close]')?.addEventListener('click', () => this.closeSheet());
    this.sheet.querySelectorAll<HTMLButtonElement>('[data-driver]').forEach((b) =>
      b.addEventListener('click', () => {
        this.selected = Number(b.dataset.driver);
        this.renderSheet();
      }),
    );
    this.sheet.querySelectorAll<HTMLButtonElement>('[data-kind]').forEach((b) =>
      b.addEventListener('click', () => {
        const to = this.selected;
        if (to == null) return;
        this.actions.onSend(to, b.dataset.kind as MessageKind);
        this.closeSheet();
      }),
    );
  }

  private relative(c: FleetContact): number {
    return c.relativeBearing;
  }

  /* --------------------------- incoming prompt --------------------------- */

  /** Show a yes/no prompt. Queues if one is already up. */
  showIncoming(msg: QuickMessage): void {
    this.promptQueue.push(msg);
    if (this.prompt.hidden) this.nextPrompt();
  }

  private nextPrompt(): void {
    const msg = this.promptQueue.shift();
    if (!msg) {
      this.prompt.hidden = true;
      return;
    }

    this.prompt.hidden = false;
    this.prompt.className = `msg-prompt msg-prompt--${msg.kind}`;
    this.prompt.innerHTML = `
      <div class="msg-prompt__from">${escapeHtml(msg.fromName)}</div>
      <div class="msg-prompt__body">
        <span class="msg-prompt__icon">${KINDS.find((k) => k.kind === msg.kind)?.icon ?? ''}</span>
        <span>${t(`msg.${msg.kind}`)}</span>
      </div>
      <div class="msg-prompt__actions">
        <button class="btn btn--big msg-prompt__no" data-reply="no">${t('msg.no')}</button>
        <button class="btn btn--big btn--primary msg-prompt__yes" data-reply="yes">${t('msg.yes')}</button>
      </div>`;

    const answer = (reply: MessageReply): void => {
      if (this.promptTimer !== null) clearTimeout(this.promptTimer);
      this.actions.onReply(msg.id, reply);
      this.nextPrompt();
    };
    this.prompt
      .querySelectorAll<HTMLButtonElement>('[data-reply]')
      .forEach((b) => b.addEventListener('click', () => answer(b.dataset.reply as MessageReply)));

    // Don't leave a question covering the map forever if nobody answers.
    if (this.promptTimer !== null) clearTimeout(this.promptTimer);
    this.promptTimer = window.setTimeout(() => this.nextPrompt(), 90_000);
  }
}

const X_SVG = `<svg viewBox="0 0 24 24"><path d="M6 6 L18 18 M18 6 L6 18" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/></svg>`;

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}
